"use client";

import React, { useState } from "react";
import {
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  MapPin,
  Send,
  Mic,
  Lightbulb,
  MapPin as LocationPin,
  Users,
  Eye,
  DollarSign,
  MessageCircle,
  Clock,
  Activity,
  X,
  Home,
  ChevronDown,
  Edit3,
} from "lucide-react";
import VoiceNoteWidget from "@/components/VoiceNoteWidget";
import { trpc } from "@/lib/trpc/client";

interface ContactFormProps {
  labels: {
    // Form labels
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    interestLabel: string;
    interestPlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    submitButton: string;
    sending: string;
    successMessage: string;
    errorMessage: string;
    separatorLabel: string;

    // Interest options
    interests: {
      cultural: string;
      nature: string;
      culinary: string;
      wellness: string;
      adventure: string;
      couples: string;
      solo: string;
      family: string;
      custom: string;
    };

    // Voice widget
    voiceWidget: {
      title: string;
      aiTitle: string;
      aiDescription: string;
      promptsTitle: string;
    };

    // Prompts
    prompts: {
      where: string;
      who: string;
      when: string;
      mustSee: string;
      avoid: string;
      accommodation: string;
      activities: string;
      budget: string;
      special: string;
    };

    // Other ways section
    otherWays: {
      heading: string;
      consultation: {
        title: string;
        button: string;
      };
      messaging: {
        title: string;
        whatsapp: string;
        telegram: string;
      };
    };

    // Next steps
    nextSteps: {
      title: string;
      steps: string[];
    };
  };
}

export function ContactForm({ labels }: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    travelInterest: "",
    message: "",
    errorMessage: "",
  });
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [showVoiceWidget, setShowVoiceWidget] = useState(false);

  const submitMutation = trpc.contact.submit.useMutation({
    onSuccess: () => {
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        travelInterest: "",
        message: "",
        errorMessage: "",
      });
    },
    onError: (error) => {
      setSubmitStatus("error");
      setFormData({
        ...formData,
        errorMessage: error.message || "Failed to send message",
      });
    },
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("idle");

    submitMutation.mutate({
      name: formData.name,
      email: formData.email,
      travelInterest: formData.travelInterest || undefined,
      message: formData.message,
    });
  };

  const handleVoiceTranscriptionComplete = (
    transcript: string,
    fullMessage: string
  ) => {
    // Populate the message field with the voice input
    setFormData((prev) => ({
      ...prev,
      message: prev.message ? `${prev.message}\n\n${fullMessage}` : fullMessage,
    }));
    setShowVoiceWidget(false);
  };

  const handleVoiceDiscard = () => {
    setShowVoiceWidget(false);
  };

  return (
    <div className="space-y-8">
      {/* Main Contact Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
          {/* Two-Column Layout: Voice Message and Text Form */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Column: Voice Message Section */}
            <div className="bg-primary-50 rounded-xl border border-primary-200 p-4 space-y-3 lg:col-span-1">
              {/* AI Info Box */}
              <div className="bg-gradient-to-r from-blue-100 via-purple-50 to-indigo-100 rounded-lg p-3 border border-blue-200">
                <div className="flex items-start space-x-2">
                  <div className="bg-blue-200 rounded-full p-1 flex-shrink-0">
                    <svg
                      className="h-3 w-3 text-blue-700"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-blue-900 font-semibold text-xs mb-1">
                      {labels.voiceWidget.aiTitle}
                    </h4>
                    <p className="text-blue-800 text-xs leading-relaxed">
                      {labels.voiceWidget.aiDescription}
                    </p>
                  </div>
                </div>
              </div>

              {/* Prompts Section - Compact */}
              <div className="bg-white rounded-lg p-2.5 border border-primary-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="h-3 w-3 text-primary-600 flex-shrink-0" />
                  <h4 className="font-semibold text-primary-900 text-xs">
                    {labels.voiceWidget.promptsTitle}
                  </h4>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div className="flex items-center space-x-1.5">
                    <LocationPin className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.where}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Users className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.who}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Clock className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.when}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Eye className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.mustSee}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <X className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.avoid}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Home className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.accommodation}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Activity className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.activities}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <DollarSign className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.budget}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5 col-span-2">
                    <MessageCircle className="h-2.5 w-2.5 text-primary-600 flex-shrink-0" />
                    <span className="text-gray-700 text-xs" style={{ color: "#374151" }}>
                      {labels.prompts.special}
                    </span>
                  </div>
                </div>
              </div>

              {/* Voice Message Button */}
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowVoiceWidget(!showVoiceWidget)}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-550 transition-colors text-sm w-full justify-center"
                >
                  <Mic className="h-4 w-4" />
                  <span>{labels.voiceWidget.title}</span>
                </button>
              </div>

              {/* Voice Widget - Always visible */}
              {showVoiceWidget && (
                <div className="mt-3">
                  <VoiceNoteWidget
                    onTranscriptionComplete={handleVoiceTranscriptionComplete}
                    onDiscard={handleVoiceDiscard}
                  />
                </div>
              )}
            </div>

            {/* Right Column: Text Form Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 lg:col-span-2">
              <div className="flex items-center space-x-2 mb-2">
                <Edit3 className="h-5 w-5 text-primary-600 flex-shrink-0" />
                <h3 className="text-base font-serif text-primary-900">
                  Type Your Message
                </h3>
              </div>

              {/* Name and Email Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
                style={{ color: "#374151" }}
              >
                {labels.nameLabel} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder={labels.namePlaceholder}
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
                style={{ color: "#374151" }}
              >
                {labels.emailLabel} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder={labels.emailPlaceholder}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="travelInterest"
              className="block text-sm font-medium text-gray-700 mb-1"
              style={{ color: "#374151" }}
            >
              {labels.interestLabel}
            </label>
            <div className="relative">
              <select
                id="travelInterest"
                name="travelInterest"
                value={formData.travelInterest}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none pr-10"
              >
                <option value="">{labels.interestPlaceholder}</option>
                <option value="cultural-immersion">
                  {labels.interests.cultural}
                </option>
                <option value="nature-wildlife">
                  {labels.interests.nature}
                </option>
                <option value="culinary-journey">
                  {labels.interests.culinary}
                </option>
                <option value="wellness-retreat">
                  {labels.interests.wellness}
                </option>
                <option value="adventure-outdoor">
                  {labels.interests.adventure}
                </option>
                <option value="couples-retreat">
                  {labels.interests.couples}
                </option>
                <option value="solo-travel">{labels.interests.solo}</option>
                <option value="family-travel">{labels.interests.family}</option>
                <option value="custom">{labels.interests.custom}</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 mb-1"
              style={{ color: "#374151" }}
            >
              {labels.messageLabel}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-base"
              placeholder={labels.messagePlaceholder}
            />
          </div>

          <button
            type="submit"
            disabled={submitMutation.isPending}
            className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-550 transition-colors flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitMutation.isPending ? labels.sending : labels.submitButton}
            <Send className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
          </button>
            </div>
          </div>

          {/* Status Messages */}
          {submitStatus === "success" && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-center">
                {labels.successMessage}
              </p>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-center">
                {formData.errorMessage || labels.errorMessage}
              </p>
            </div>
          )}
        </form>

      {/* Other Ways to Connect - Moved below form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-serif text-primary-900 mb-6">
              {labels.otherWays.heading}
            </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Book Consultation */}
              <div>
            <div className="flex items-center space-x-3 mb-3">
                  <Calendar className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <h4 className="font-semibold text-primary-900 text-sm">
                    {labels.otherWays.consultation.title}
                  </h4>
                </div>
                <a
                  href="https://calendly.com/lesia-revoyager/30min"
                  target="_blank"
                  rel="noopener noreferrer"
              className="inline-block bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-550 transition-colors text-sm w-full text-center"
                >
                  {labels.otherWays.consultation.button}
                </a>
              </div>

              {/* WhatsApp & Telegram */}
              <div>
            <div className="flex items-center space-x-3 mb-3">
                  <MessageSquare className="h-5 w-5 text-primary-600 flex-shrink-0" />
                  <h4 className="font-semibold text-primary-900 text-sm">
                    {labels.otherWays.messaging.title}
                  </h4>
                </div>
            <div className="space-y-2">
                  <a
                    href="https://wa.me/491785484825"
                className="block bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors text-sm text-center"
                  >
                    {labels.otherWays.messaging.whatsapp}
                  </a>
                  <a
                    href="https://t.me/revoyager_travel"
                className="block bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm text-center"
                  >
                    {labels.otherWays.messaging.telegram}
                  </a>
                </div>
              </div>

              {/* Direct Contact */}
          <div>
            <h4 className="font-semibold text-primary-900 text-sm mb-3">
              Contact Details
            </h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-primary-600 flex-shrink-0" />
                  <span
                  className="text-gray-700 text-sm"
                    style={{ color: "#374151" }}
                  >
                    hello@revoyager.com
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-primary-600 flex-shrink-0" />
                  <span
                  className="text-gray-700 text-sm"
                    style={{ color: "#374151" }}
                  >
                    +49 1785484825
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-4 w-4 text-primary-600 flex-shrink-0" />
                  <span
                  className="text-gray-700 text-sm"
                    style={{ color: "#374151" }}
                  >
                    Berlin, Germany
                  </span>
              </div>
            </div>
          </div>

          {/* What happens next */}
          <div className="bg-primary-50 rounded-lg p-4">
            <h4 className="font-semibold text-primary-900 text-sm mb-3">
              {labels.nextSteps.title}
            </h4>
            <ul className="space-y-2">
              {labels.nextSteps.steps.map((step, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  <span
                    className="text-gray-700 text-xs leading-relaxed"
                    style={{ color: "#374151" }}
                  >
                    {step}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
